import {
	IconArrowRight,
	IconBook2,
	IconCalendarEvent,
	IconCheck,
	IconDownload,
	IconIdBadge2,
	IconPalette,
	IconSparkles,
	IconTicket,
	IconVideo,
} from "@tabler/icons-react"
import { motion, useReducedMotion } from "motion/react"
import { type ComponentType, type FC, useEffect, useRef } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

import Link from "~/components/Link"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { showConfetti } from "~/utilities/celebration"
import routes from "~/utilities/routes"

import Layout, { Logo } from "./Layout"

type ProductChoice = {
	title: string
	description: string
	detail: string
	href: string
	icon: ComponentType<{ className?: string }>
	accent: string
}

const AcademyCreatedPage: FC = () => {
	const user = useUser().user!
	const platform = usePlatform()!
	const celebrationRef = useRef<HTMLDivElement>(null)
	const prefersReducedMotion = useReducedMotion()

	useEffect(() => {
		if (prefersReducedMotion) return

		const timeout = window.setTimeout(async () => {
			const celebration = celebrationRef.current
			if (celebration === null) return

			await showConfetti(celebration)
		}, 450)

		return () => window.clearTimeout(timeout)
	}, [prefersReducedMotion])

	const productChoices: ProductChoice[] = [
		{
			title: "Course",
			description: "Teach something step by step",
			detail: "Lessons, chapters and quizzes",
			href: `${routes.course.index()}?create=true`,
			icon: IconBook2,
			accent: "bg-[#fff0bc] text-[#8a5d00]",
		},
		{
			title: "Downloadable",
			description: "Sell a file or digital resource",
			detail: "E-books, templates and guides",
			href: routes.downloadable.create(),
			icon: IconDownload,
			accent: "bg-[#dff7e8] text-[#167246]",
		},
		{
			title: "Video",
			description: "Share a standalone video",
			detail: "Tutorials, talks and recordings",
			href: routes.video.create(),
			icon: IconVideo,
			accent: "bg-[#e5e8ff] text-[#4452a8]",
		},
		{
			title: "Coaching session",
			description: "Offer your time and expertise",
			detail: "Calls, consulting and mentoring",
			href: routes.sessions.create(),
			icon: IconCalendarEvent,
			accent: "bg-[#ffe3dc] text-[#a5412b]",
		},
		{
			title: "Event",
			description: "Bring your audience together live",
			detail: "Workshops, webinars and meetups",
			href: `${routes.main.event()}?create=true`,
			icon: IconTicket,
			accent: "bg-[#dff4ff] text-[#176b91]",
		},
		{
			title: "Membership",
			description: "Create recurring access",
			detail: "Tiers, subscriptions and exclusives",
			href: `${routes.membership.index()}?create=true`,
			icon: IconIdBadge2,
			accent: "bg-[#f4e7ff] text-[#7540a4]",
		},
	]

	return (
		<Layout>
			<main className="flex w-full max-w-5xl flex-col items-center py-8 sm:py-12">
				<Logo className="mb-8" />
				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.7, rotate: -8 }}
					animate={{ opacity: 1, scale: 1, rotate: 0 }}
					transition={{ type: "spring", stiffness: 240, damping: 16 }}
					className="relative mb-5"
				>
					<div
						ref={celebrationRef}
						className="flex size-20 items-center justify-center rounded-full bg-[#d9f99d] text-[#365314] shadow-[0_12px_32px_rgba(63,98,18,0.18)] ring-8 ring-[#f4ffd9]"
					>
						<IconCheck className="size-10 stroke-[2.5]" />
					</div>
					<IconSparkles className="absolute -top-3 -right-7 size-7 text-[#f59e0b]" />
				</motion.div>

				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.12, duration: 0.4 }}
					className="flex max-w-2xl flex-col items-center text-center"
				>
					<p className="mb-3 rounded-full bg-[#ecfccb] px-3 py-1 text-xs font-bold tracking-[0.16em] text-[#4d7c0f] uppercase">
						Academy created
					</p>
					<h1 className="text-3xl leading-tight font-bold tracking-[-0.025em] text-slate-950 sm:text-5xl">
						You did it, {user.firstName}.
					</h1>
					<p className="mt-3 max-w-xl text-base leading-relaxed font-medium text-slate-500 sm:text-lg">
						<strong className="text-slate-700">{platform.name}</strong> is ready. Turn that momentum into
						your first piece of content.
					</p>
				</motion.div>

				<motion.section
					initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.25, duration: 0.45 }}
					className="mt-9 w-full rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-7"
				>
					<div className="mb-5 flex flex-wrap items-end justify-between gap-3">
						<div>
							<p className="text-primary text-xs font-bold tracking-[0.16em] uppercase">Next milestone</p>
							<h2 className="mt-1 text-2xl font-bold tracking-[-0.02em] text-slate-900">
								What will you create first?
							</h2>
						</div>
						<div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
							<span className="flex size-5 items-center justify-center rounded-full bg-[#d9f99d] text-[#3f6212]">
								<IconCheck className="size-3.5" />
							</span>
							Academy
							<span className="h-px w-5 bg-slate-200" />
							<span className="bg-primary flex size-5 items-center justify-center rounded-full text-white">
								2
							</span>
							First product
						</div>
					</div>

					<div className="flex flex-wrap gap-3">
						{productChoices.map((choice, index) => (
							<motion.div
								key={choice.title}
								initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.34 + index * 0.07, duration: 0.35 }}
								className="flex basis-full sm:basis-[calc((100%_-_0.75rem)/2)] lg:basis-[calc((100%_-_1.5rem)/3)]"
							>
								<Link
									to={choice.href}
									className="group focus-visible:outline-primary flex w-full cursor-pointer flex-col rounded-2xl border border-slate-200 bg-[#fcfcfb] p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-[0_12px_28px_rgba(15,23,42,0.09)] focus-visible:outline-2 focus-visible:outline-offset-2"
								>
									<div
										className={cn(
											"mb-5 flex size-11 items-center justify-center rounded-xl",
											choice.accent,
										)}
									>
										<choice.icon className="size-5" />
									</div>
									<div className="flex items-center justify-between gap-3">
										<h3 className="text-lg font-bold text-slate-900">{choice.title}</h3>
										<IconArrowRight className="group-hover:text-primary size-5 shrink-0 text-slate-300 transition-transform duration-200 group-hover:translate-x-1" />
									</div>
									<p className="mt-1 text-sm font-medium text-slate-600">{choice.description}</p>
									<p className="mt-3 text-xs text-slate-400">{choice.detail}</p>
								</Link>
							</motion.div>
						))}
					</div>

					<motion.div
						initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.34 + productChoices.length * 0.07, duration: 0.35 }}
						className="mt-3"
					>
						<Link
							to={routes.settings.customizeAcademy()}
							className="group focus-visible:outline-primary flex w-full cursor-pointer items-center gap-4 rounded-2xl border border-dashed border-slate-200 bg-[#fcfcfb] p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-[0_12px_28px_rgba(15,23,42,0.09)] focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							<div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#fce7f3] text-[#9d2464]">
								<IconPalette className="size-5" />
							</div>
							<div className="min-w-0 flex-1">
								<h3 className="text-lg font-bold text-slate-900">Set up your academy branding</h3>
								<p className="mt-0.5 text-sm font-medium text-slate-600">
									Prefer to customize your academy first? Add your logo, colors and domain. You can
									create your first product anytime.
								</p>
							</div>
							<IconArrowRight className="group-hover:text-primary size-5 shrink-0 text-slate-300 transition-transform duration-200 group-hover:translate-x-1" />
						</Link>
					</motion.div>
				</motion.section>

				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.9, duration: 0.35 }}
					className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-sm text-slate-500"
				>
					<span>Not ready to choose?</span>
					<Link
						to={routes.main.index()}
						className="hover:text-primary cursor-pointer font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 transition-colors"
					>
						Go to dashboard
					</Link>
				</motion.div>
			</main>
		</Layout>
	)
}

export default AcademyCreatedPage
